import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  FaBell,
  FaChevronDown,
  FaCompass,
  FaEllipsisH,
  FaMapMarkedAlt,
  FaPlus,
  FaRoute,
  FaSatelliteDish,
  FaUserCircle,
  FaUsers
} from 'react-icons/fa'

import { useAuth } from 'contexts/auth.context'
import { useUser } from 'contexts/user.context'

import api from 'services/api'

import { RegisterModal } from './components'
import LoginModal from 'components/modal'

import {
  WrapperHeader,
  HeaderInner,
  BrandLink,
  Image,
  NavCapsule,
  NavItem,
  RightArea,
  CreateRouteLink,
  UserMenu,
  UserTrigger,
  ChevronIcon,
  DropdownMenu,
  DropdownLink,
  DropdownButton,
  AuthDiv,
  RegistryLink,
  ModalButton,
  NotificationMenu,
  NotificationButton,
  NotificationBadge,
  NotificationPanel,
  NotificationHeader,
  NotificationList,
  NotificationItem,
  NotificationAvatar,
  NotificationBody,
  NotificationActions,
  NotificationEmpty,
  MobileMenuButton,
  MobileMenuOverlay,
  MobileMenuSheet,
  MobileMenuHeader,
  MobileMenuClose,
  MobileMenuTitle,
  MobileNav,
  MobileNavLink,
  MobileNavButton,
  MobilePrimaryLink,
  MobileAuthRow,
  MobileAuthButton,
  MobileSection,
  MobileSectionTitle,
  MobileNotificationList,
  MobileNotificationEntry
} from './style'

type HeaderProps = { authenticated?: boolean }

type NotificationItemType = {
  id: string
  type:
    | 'collaboration_invite'
    | 'new_follower'
    | 'itinerary_inspired'
    | 'itinerary_rated'
    | 'itinerary_favorited'
    | 'collaborative_itinerary_updated'
    | 'itinerary_commented'
    | 'comment_replied'
  entityId?: string | null
  entityTitle?: string | null
  readAt?: string | null
  createdAt: string
  actorId?: string | null
  actorFirstname?: string | null
  actorLastname?: string | null
  actorProfilePicture?: string | null
  actionStatus?: 'pending' | 'accepted' | 'rejected' | null
}

const Header = ({ authenticated }: HeaderProps) => {
  const { signOut } = useAuth()
  const { user } = useUser()
  const location = useLocation()
  const history = useHistory()
  const userMenuRef = useRef<HTMLDivElement>(null)
  const notificationMenuRef = useRef<HTMLDivElement>(null)

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isRegisterModalVisible, setIsRegisterModalVisible] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [notifications, setNotifications] = useState<NotificationItemType[]>([])
  const [unreadCount, setUnreadCount] = useState(0)

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const firstName = user?.firstname || 'Minha conta'
  const isStaff = Boolean(
    user?.isAdmin ||
      user?.isMaster ||
      user?.isPublisher ||
      user?.isCommercial ||
      user?.isStaff
  )
  const staffEntry = user?.isPublisher
    ? '/admin/radar'
    : user?.isCommercial
      ? '/admin/anuncios'
      : '/admin'

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`)

  useEffect(() => {
    if (query.get('isLogin')) setIsModalVisible(true)
    if (query.get('isRegister')) setIsRegisterModalVisible(true)
  }, [query])

  const loadNotifications = async () => {
    if (!authenticated) return

    try {
      const response = await api.get('/notifications')
      setNotifications(response.data?.items ?? [])
      setUnreadCount(Number(response.data?.unreadCount ?? 0))
    } catch {
      setNotifications([])
      setUnreadCount(0)
    }
  }

  useEffect(() => {
    if (!authenticated) return

    loadNotifications()
    const interval = window.setInterval(loadNotifications, 60000)
    return () => window.clearInterval(interval)
  }, [authenticated])

  useEffect(() => {
    setIsMobileMenuOpen(false)
    setIsNotificationsOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!isMobileMenuOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isMobileMenuOpen])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false)
      }

      if (
        notificationMenuRef.current &&
        !notificationMenuRef.current.contains(event.target as Node)
      ) {
        setIsNotificationsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  const markRead = async (notificationId: string) => {
    await api.patch(`/notifications/${notificationId}/read`)
    setNotifications((current) =>
      current.map((item) =>
        item.id === notificationId
          ? { ...item, readAt: item.readAt || new Date().toISOString() }
          : item
      )
    )
    setUnreadCount((current) => Math.max(0, current - 1))
  }

  const markAllRead = async () => {
    await api.patch('/notifications/read-all')
    setNotifications((current) =>
      current.map((item) => ({
        ...item,
        readAt: item.readAt || new Date().toISOString()
      }))
    )
    setUnreadCount(0)
  }

  const respondToCollaboration = async (
    notification: NotificationItemType,
    action: 'accept' | 'reject'
  ) => {
    if (!notification.entityId) return

    await api.post(
      `/itineraries/${notification.entityId}/collaborators/respond`,
      { action }
    )
    await loadNotifications()
  }

  const openNotification = async (notification: NotificationItemType) => {
    if (!notification.readAt) {
      await markRead(notification.id)
    }

    setIsNotificationsOpen(false)

    if (notification.type === 'new_follower' && notification.actorId) {
      history.push(`/usuario/${notification.actorId}`)
      return
    }

    if (
      [
        'itinerary_inspired',
        'itinerary_rated',
        'itinerary_favorited',
        'collaborative_itinerary_updated',
        'itinerary_commented',
        'comment_replied'
      ].includes(notification.type) &&
      notification.entityId
    ) {
      history.push(`/roteiro/${notification.entityId}`)
      return
    }

    if (
      notification.type === 'collaboration_invite' &&
      notification.actionStatus === 'accepted' &&
      notification.entityId
    ) {
      history.push(`/roteiro/${notification.entityId}`)
    }
  }

  const notificationText = (notification: NotificationItemType) => {
    const actor =
      [notification.actorFirstname, notification.actorLastname]
        .filter(Boolean)
        .join(' ') || 'Um viajante'

    if (notification.type === 'new_follower') {
      return `${actor} começou a seguir você.`
    }

    if (notification.type === 'collaboration_invite') {
      return `${actor} convidou você para colaborar em “${notification.entityTitle || 'um roteiro'}”.`
    }

    if (notification.type === 'itinerary_rated') {
      return `${actor} avaliou seu roteiro “${notification.entityTitle || 'um roteiro'}”.`
    }

    if (notification.type === 'itinerary_favorited') {
      return `${actor} favoritou seu roteiro “${notification.entityTitle || 'um roteiro'}”.`
    }

    if (notification.type === 'collaborative_itinerary_updated') {
      return `${actor} modificou o roteiro colaborativo “${notification.entityTitle || 'um roteiro'}”.`
    }

    if (notification.type === 'itinerary_commented') {
      return `${actor} comentou no seu roteiro “${notification.entityTitle || 'um roteiro'}”.`
    }

    if (notification.type === 'comment_replied') {
      return `${actor} respondeu seu comentário em “${notification.entityTitle || 'um roteiro'}”.`
    }

    return `${actor} se inspirou no seu roteiro “${notification.entityTitle || ''}”.`
  }

  return (
    <>
      <LoginModal
        isOpen={isModalVisible}
        closeModal={() => setIsModalVisible(false)}
      />
      <RegisterModal
        isOpen={isRegisterModalVisible}
        closeModal={() => setIsRegisterModalVisible(false)}
      />

      <WrapperHeader>
        <HeaderInner>
          <BrandLink to="/" aria-label="Ir para a página inicial">
            <Image src="/images/header-logo.png" alt="RedSterna" />
          </BrandLink>

          <MobileMenuButton
            type="button"
            aria-label="Abrir menu principal"
            aria-expanded={isMobileMenuOpen}
            onClick={() => {
              setIsMobileMenuOpen(true)
              setIsUserMenuOpen(false)
              setIsNotificationsOpen(false)
            }}
          >
            <FaEllipsisH />
          </MobileMenuButton>

          <NavCapsule aria-label="Navegação principal">
            <NavItem to="/destinos" $active={isActive('/destinos')}>
              <FaMapMarkedAlt />
              <span>Destinos</span>
            </NavItem>

            <NavItem to="/roteiros" $active={isActive('/roteiros')}>
              <FaRoute />
              <span>Roteiros</span>
            </NavItem>

            <NavItem
              to="/dicas-de-viagem"
              $active={isActive('/dicas-de-viagem')}
            >
              <FaCompass />
              <span>Planeje</span>
            </NavItem>

            <NavItem to="/radar" $active={isActive('/radar')}>
              <FaSatelliteDish />
              <span>Radar</span>
            </NavItem>

            <NavItem
              to="/buscar-usuarios"
              $active={isActive('/buscar-usuarios')}
            >
              <FaUsers />
              <span>Viajantes</span>
            </NavItem>
          </NavCapsule>

          <RightArea>
            {!authenticated ? (
              <AuthDiv>
                <RegistryLink onClick={() => setIsRegisterModalVisible(true)}>
                  Cadastrar-se
                </RegistryLink>
                <ModalButton onClick={() => setIsModalVisible(true)}>
                  Login
                </ModalButton>
              </AuthDiv>
            ) : (
              <>
                <CreateRouteLink to="/criar-roteiro">
                  <FaPlus />
                  <span>Criar roteiro</span>
                </CreateRouteLink>

                <NotificationMenu ref={notificationMenuRef}>
                  <NotificationButton
                    type="button"
                    aria-label="Abrir notificações"
                    aria-expanded={isNotificationsOpen}
                    onClick={() => {
                      setIsNotificationsOpen((current) => !current)
                      setIsUserMenuOpen(false)
                      if (!isNotificationsOpen) loadNotifications()
                    }}
                  >
                    <FaBell />
                    {unreadCount > 0 && (
                      <NotificationBadge>
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </NotificationBadge>
                    )}
                  </NotificationButton>

                  {isNotificationsOpen && (
                    <NotificationPanel>
                      <NotificationHeader>
                        <div>
                          <strong>Notificações</strong>
                          <span>
                            {unreadCount > 0
                              ? `${unreadCount} não ${unreadCount === 1 ? 'lida' : 'lidas'}`
                              : 'Tudo em dia'}
                          </span>
                        </div>
                        {unreadCount > 0 && (
                          <button type="button" onClick={markAllRead}>
                            Marcar como lidas
                          </button>
                        )}
                      </NotificationHeader>

                      {notifications.length > 0 ? (
                        <NotificationList>
                          {notifications.map((notification) => (
                            <NotificationItem
                              key={notification.id}
                              $unread={!notification.readAt}
                            >
                              <NotificationAvatar
                                src={
                                  notification.actorProfilePicture ||
                                  '/images/profile-empty.png'
                                }
                                alt=""
                              />
                              <NotificationBody
                                type="button"
                                onClick={() => openNotification(notification)}
                              >
                                <strong>
                                  {notificationText(notification)}
                                </strong>
                                <span>
                                  {new Date(
                                    notification.createdAt
                                  ).toLocaleDateString('pt-BR', {
                                    day: '2-digit',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </NotificationBody>

                              {notification.type === 'collaboration_invite' &&
                                notification.actionStatus === 'pending' && (
                                  <NotificationActions>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        respondToCollaboration(
                                          notification,
                                          'reject'
                                        )
                                      }
                                    >
                                      Recusar
                                    </button>
                                    <button
                                      type="button"
                                      className="primary"
                                      onClick={() =>
                                        respondToCollaboration(
                                          notification,
                                          'accept'
                                        )
                                      }
                                    >
                                      Aceitar
                                    </button>
                                  </NotificationActions>
                                )}
                            </NotificationItem>
                          ))}
                        </NotificationList>
                      ) : (
                        <NotificationEmpty>
                          Você não tem novas notificações.
                        </NotificationEmpty>
                      )}
                    </NotificationPanel>
                  )}
                </NotificationMenu>

                <UserMenu ref={userMenuRef}>
                  <UserTrigger
                    type="button"
                    onClick={() => setIsUserMenuOpen((current) => !current)}
                    aria-expanded={isUserMenuOpen}
                    aria-label="Abrir menu da conta"
                  >
                    <FaUserCircle />
                    <span>{firstName}</span>
                    <ChevronIcon $open={isUserMenuOpen}>
                      <FaChevronDown />
                    </ChevronIcon>
                  </UserTrigger>

                  {isUserMenuOpen && (
                    <DropdownMenu>
                      <DropdownLink
                        to="/minha-conta"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Meu perfil
                      </DropdownLink>
                      <DropdownLink
                        to="/editar-perfil"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Editar perfil
                      </DropdownLink>
                      {isStaff && (
                        <DropdownLink
                          to={staffEntry}
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Área de gestão
                        </DropdownLink>
                      )}
                      <DropdownButton type="button" onClick={() => signOut()}>
                        Sair
                      </DropdownButton>
                    </DropdownMenu>
                  )}
                </UserMenu>
              </>
            )}
          </RightArea>
        </HeaderInner>
      </WrapperHeader>

      {isMobileMenuOpen && (
        <MobileMenuOverlay
          role="presentation"
          onMouseDown={() => setIsMobileMenuOpen(false)}
        >
          <MobileMenuSheet
            role="dialog"
            aria-modal="true"
            aria-label="Menu principal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <MobileMenuHeader>
              <div>
                <MobileMenuTitle>Menu</MobileMenuTitle>
                <span>Explore a RedSterna</span>
              </div>
              <MobileMenuClose
                type="button"
                aria-label="Fechar menu"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                ×
              </MobileMenuClose>
            </MobileMenuHeader>

            <MobileNav>
              <MobileNavLink
                to="/destinos"
                $active={isActive('/destinos')}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <FaMapMarkedAlt />
                Destinos
              </MobileNavLink>
              <MobileNavLink
                to="/roteiros"
                $active={isActive('/roteiros')}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <FaRoute />
                Roteiros
              </MobileNavLink>
              <MobileNavLink
                to="/dicas-de-viagem"
                $active={isActive('/dicas-de-viagem')}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <FaCompass />
                Planeje
              </MobileNavLink>
              <MobileNavLink
                to="/radar"
                $active={isActive('/radar')}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <FaSatelliteDish />
                Radar
              </MobileNavLink>
              <MobileNavLink
                to="/buscar-usuarios"
                $active={isActive('/buscar-usuarios')}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <FaUsers />
                Viajantes
              </MobileNavLink>
            </MobileNav>

            {!authenticated ? (
              <MobileSection>
                <MobileSectionTitle>Sua conta</MobileSectionTitle>
                <MobileAuthRow>
                  <MobileAuthButton
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false)
                      setIsRegisterModalVisible(true)
                    }}
                  >
                    Cadastrar-se
                  </MobileAuthButton>
                  <MobileAuthButton
                    type="button"
                    className="primary"
                    onClick={() => {
                      setIsMobileMenuOpen(false)
                      setIsModalVisible(true)
                    }}
                  >
                    Entrar
                  </MobileAuthButton>
                </MobileAuthRow>
              </MobileSection>
            ) : (
              <>
                <MobileSection>
                  <MobilePrimaryLink
                    to="/criar-roteiro"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FaPlus />
                    Criar roteiro
                  </MobilePrimaryLink>
                </MobileSection>

                <MobileSection>
                  <MobileSectionTitle>Conta</MobileSectionTitle>
                  <MobileNav>
                    <MobileNavLink
                      to="/minha-conta"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <FaUserCircle />
                      Meu perfil
                    </MobileNavLink>
                    <MobileNavLink
                      to="/editar-perfil"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <FaUserCircle />
                      Editar perfil
                    </MobileNavLink>
                    {isStaff && (
                      <MobileNavLink
                        to={staffEntry}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <FaCompass />
                        Área de gestão
                      </MobileNavLink>
                    )}
                    <MobileNavButton
                      type="button"
                      onClick={() => {
                        setIsNotificationsOpen((current) => !current)
                        if (!isNotificationsOpen) loadNotifications()
                      }}
                    >
                      <FaBell />
                      Notificações
                      {unreadCount > 0 && (
                        <strong>{unreadCount > 9 ? '9+' : unreadCount}</strong>
                      )}
                    </MobileNavButton>
                  </MobileNav>

                  {isNotificationsOpen && (
                    <MobileNotificationList>
                      {notifications.length ? (
                        notifications.slice(0, 6).map((notification) => (
                          <MobileNotificationEntry key={notification.id}>
                            <button
                              type="button"
                              onClick={() => {
                                setIsMobileMenuOpen(false)
                                openNotification(notification)
                              }}
                            >
                              <strong>{notificationText(notification)}</strong>
                              <span>
                                {new Date(
                                  notification.createdAt
                                ).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </button>

                            {notification.type === 'collaboration_invite' &&
                              notification.actionStatus === 'pending' && (
                                <NotificationActions>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      respondToCollaboration(
                                        notification,
                                        'reject'
                                      )
                                    }
                                  >
                                    Recusar
                                  </button>
                                  <button
                                    type="button"
                                    className="primary"
                                    onClick={() =>
                                      respondToCollaboration(
                                        notification,
                                        'accept'
                                      )
                                    }
                                  >
                                    Aceitar
                                  </button>
                                </NotificationActions>
                              )}
                          </MobileNotificationEntry>
                        ))
                      ) : (
                        <NotificationEmpty>
                          Você não tem novas notificações.
                        </NotificationEmpty>
                      )}
                    </MobileNotificationList>
                  )}

                  <MobileNavButton
                    type="button"
                    className="signout"
                    onClick={() => signOut()}
                  >
                    Sair
                  </MobileNavButton>
                </MobileSection>
              </>
            )}
          </MobileMenuSheet>
        </MobileMenuOverlay>
      )}
    </>
  )
}

export default Header
